import React, { useEffect, useRef, useState } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { COVER_SIZES, coverSrc, coverSrcSet } from '../utils/images';
interface BookCover3DProps {
  src: string;
  alt: string;
  className?: string;
  intensity?: number;
  showSpine?: boolean;
  /** Above-the-fold covers must load eagerly; everything else stays lazy. */
  loading?: 'eager' | 'lazy';
  /** Marks the LCP candidate so the browser fetches it first. */
  highPriority?: boolean;
}

/** Intrinsic size of the cover artwork, set to reserve layout space (no CLS). */
const COVER_WIDTH = 1024;
const COVER_HEIGHT = 1536;
export function BookCover3D({
  src,
  alt,
  className = '',
  intensity = 12,
  showSpine = true,
  loading = 'lazy',
  highPriority = false
}: BookCover3DProps) {
  const ref = useRef<HTMLDivElement>(null);
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  // Touch devices have no cursor: skip the tilt/glare work entirely there.
  const [pointerFine, setPointerFine] = useState(false);
  const [useOriginal, setUseOriginal] = useState(false);
  useEffect(() => {
    const query = window.matchMedia('(hover: hover) and (pointer: fine)');
    setPointerFine(query.matches);
    const onChange = (event: MediaQueryListEvent) => setPointerFine(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  const spring = {
    stiffness: 160,
    damping: 20,
    mass: 0.6
  };
  const sx = useSpring(px, spring);
  const sy = useSpring(py, spring);
  const rotateY = useTransform(sx, [-0.5, 0.5], [-intensity, intensity]);
  const rotateX = useTransform(sy, [-0.5, 0.5], [intensity, -intensity]);
  const glareX = useTransform(sx, [-0.5, 0.5], [15, 85]);
  const glareY = useTransform(sy, [-0.5, 0.5], [15, 85]);
  const glare = useTransform([glareX, glareY], ([gx, gy]: number[]) => `radial-gradient(120px circle at ${gx}% ${gy}%, rgba(255,255,255,0.22), transparent 70%)`);
  function handleMove(event: React.MouseEvent<HTMLDivElement>) {
    const bounds = ref.current?.getBoundingClientRect();
    if (!bounds) return;
    px.set((event.clientX - bounds.left) / bounds.width - 0.5);
    py.set((event.clientY - bounds.top) / bounds.height - 0.5);
  }
  function handleLeave() {
    px.set(0);
    py.set(0);
  }
  const priorityAttributes: Record<string, string> = highPriority ? {
    fetchpriority: 'high'
  } : {};
  return <div className={`perspective-[1400px] ${className}`} data-id="element-7" data-mp="2ac8h0kfgzsyd">
      <motion.div ref={ref} onMouseMove={pointerFine ? handleMove : undefined} onMouseLeave={pointerFine ? handleLeave : undefined} style={pointerFine ? {
      rotateX,
      rotateY
    } : undefined} className="preserve-3d relative" data-id="element-8" data-mp="2feh9lfofkszy">
        <div className="preserve-3d relative overflow-hidden rounded-[4px] rounded-l-[2px] ring-1 ring-gold-500/25 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.95),0_10px_20px_-10px_rgba(192,24,44,0.35)]" data-id="element-9" data-mp="1ro3d9cytc7zk">
          <img src={useOriginal ? src : coverSrc(src)} srcSet={useOriginal ? undefined : coverSrcSet(src)} sizes={useOriginal ? undefined : COVER_SIZES} onError={() => setUseOriginal(true)} alt={alt} width={COVER_WIDTH} height={COVER_HEIGHT} loading={loading} decoding={loading === 'eager' ? 'auto' : 'async'} {...priorityAttributes} className="block h-auto w-full select-none" draggable={false} data-id="element-10" data-mp="1o5nxmtqfkibb" />
          {showSpine && <>
              <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 w-[9px] bg-black/70" data-id="element-11" data-mp="vgz014ol2v4b" />
              <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-[9px] w-[2px] bg-white/10" data-id="element-12" data-mp="rdk9vl6xkyd5" />
            </>}
          {pointerFine && <motion.span aria-hidden="true" style={{
          background: glare
        }} className="pointer-events-none absolute inset-0 mix-blend-screen" data-id="element-13" data-mp="2hjqm0f61roey" />}
        </div>
        <span aria-hidden="true" className="pointer-events-none absolute -bottom-6 left-1/2 h-6 w-[78%] -translate-x-1/2 rounded-[50%] bg-black/70 blur-lg" data-id="element-14" data-mp="27ufa6krs05nd" />
      </motion.div>
    </div>;
}